import { ChangeDetectorRef } from '@angular/core'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { WorkspaceRoleService } from '@auth/services/workspace-role.service'
import { DatabaseService } from '@database/services/database.service'
import { ServerRepresentedEvent } from '@events/models/event.model'
import { Persona } from '@personas/interfaces/persona.interface'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { Registration } from './registration.model'
import { RegistrationsDataSource } from './registrations.datasource'
import { RegistrationsDataSourceCollectionConfig } from './registrations.datasource-collection.config'
import { RegistrationsRequest } from './registrations.request.interface'
/**
 * Defines a collection of multiple registrations data sources, main class for bootstrapping an event database
 */
export class RegistrationsDataSourceCollection {
  /** Event slug */
  private _eventSlug: string
  /** Event checkin spot - global */
  private _eventCheckinSpot: string
  /** Selected datasource index */
  _selectedIndex = 0
  /**
   * Stop all hot observables
   */
  private _destroy$: Subject<boolean> = new Subject()
  /**
   * Datasource collection changed event emitter
   */
  public changed$: Subject<boolean> = new Subject()
  /**
   * Clean up the subscriptions
   */
  public destroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }
  /** Returns the selected data source index */
  get selectedIndex() {
    return this._selectedIndex
  }
  /** Sets the selected datasource index and fires it's initialize method, efficiently getting the registrations */
  set selectedIndex(value) {
    if (value === this._selectedIndex) {
      return
    }
    this._selectedIndex = value
    this.registrationsDataSources[value].registrationsDataSource.initialize(this.registrationsDataSources[value].registrationsDataSource.initializeRequest, true, false)
  }
  /** Default configurations for a datasource collection - used for any datasource unless otherwise specified */
  private _defaultConfig: RegistrationsDataSourceCollectionConfig = {
    perPage: 50,
    page: 1,
    searchTerm: '',
    searchAfter: []
  }
  /**
   * array of registrations data sources
   */
  public registrationsDataSources: {
    registrationsDataSource: RegistrationsDataSource
    title: string
    weight: string
    slug: string
  }[]
  /** whether any datasource has some selections */
  public get selectionExists() {
    return this.registrationsDataSources.some((o) => o.registrationsDataSource.selectionStatus !== 'None')
  }
  /** selected registrations */
  public get selected(): Registration[] {
    const selectedRegistrations = []
    this.registrationsDataSources.forEach((o) => selectedRegistrations.push(...o.registrationsDataSource.selectedRegistrants))
    return selectedRegistrations
  }
  /**
   * clears all selections in all data sources
   */
  public clearSelections() {
    this.registrationsDataSources.forEach((o) => o.registrationsDataSource.clearSelection())
  }
  /**
   *  gets a single registration datasource by it's key
   * @param key datasource key
   * @returns registration datasource
   */
  public get(key: string) {
    return this.registrationsDataSources.find((o) => o.slug === key)
  }
  /**
   * Sets a registration datasource by a key
   * @param key datasource key
   * @param registrationsDataSource registration datasource
   */
  public set(key: string, registrationsDataSource: RegistrationsDataSource) {
    this.get(key).registrationsDataSource = registrationsDataSource
  }
  /**
   * updates a data source by firing it's initialize method with a new request
   * @param key datasource key
   * @param registrationsRequest intialization registration request
   */
  public update(key: string, registrationsRequest: RegistrationsRequest) {
    this.get(key).registrationsDataSource.initialize(registrationsRequest)
  }
  /**
   * updates the current search term for all datasources
   * @param term search term
   */
  public updateSearchTerm(term: string) {
    this.registrationsDataSources.forEach((source, index) => {
      source.registrationsDataSource.initialize(
        {
          ...source.registrationsDataSource.initializeRequest,
          searchTerm: term,
          page: 1
        },
        index === this.selectedIndex
      )
    })
  }
  /**
   * Filter registrants
   * @param filter
   */
  public filterStatus(filter: { status; flagged; refunded; days?; attended? }, fetchData = true) {
    this.registrationsDataSources.forEach((source, index) => {
      source.registrationsDataSource.initialize(
        {
          ...source.registrationsDataSource.initializeRequest,
          approved: filter.status,
          flagged: filter.flagged,
          refunded: filter.refunded,
          days: filter.days,
          attended: filter.attended,
          page: 1
        },
        fetchData && index === this.selectedIndex
      )
    })
    if (!(filter.flagged || filter.status)) this.registrationsDataSources[this.selectedIndex].registrationsDataSource.allSelected = false
  }
  /**
   * Approves all selected entries of the selected datasource
   */
  public approveSelections() {
    this.registrationsDataSources[this.selectedIndex].registrationsDataSource.approveSelection()
  }

  /**
   * Mark Attended selected registrations
   */
  public bulkAttendSelection() {
    this.registrationsDataSources[this.selectedIndex].registrationsDataSource.markBulkAttended()
  }
  /**
   * Mark Unattended selected registrations
   */
  public bulkUnttendSelection() {
    this.registrationsDataSources[this.selectedIndex].registrationsDataSource.markBulkUnttended()
  }
  /**
   * Disapprovesa all selected entries of the selected datasource
   */
  public disapproveSelections() {
    this.registrationsDataSources[this.selectedIndex].registrationsDataSource.disapproveSelection()
  }
  /**
   * Flags all selected entries of selected datasource
   */
  public flagSelections() {
    this.registrationsDataSources[this.selectedIndex].registrationsDataSource.flagSelection()
  }
  /**
   * Unflags all selected entires of selected datasource
   */
  public unflagSelections() {
    this.registrationsDataSources[this.selectedIndex].registrationsDataSource.unflagSelection()
  }
  /** Deletes all selected entries of selected datasource */
  public deleteSelections() {
    this.registrationsDataSources[this.selectedIndex].registrationsDataSource.deleteSelection()
  }
  /**
   * Request revision for all selected entries of the selected datasource
   */
  public requestRevisionSelections() {
    this.registrationsDataSources[this.selectedIndex].registrationsDataSource.requestRevisionSelection()
  }
  /**
   * Creates a new registration datasource collection
   * @param service Database service
   * @param workspace workspaces slug
   * @param event Client event
   * @param forms Persona array
   * @param totalCounts Total exportable counts
   * @param config Datasource collection configuration object
   */
  constructor(
    service: DatabaseService,
    workspace: string,
    event: ServerRepresentedEvent,
    forms: Persona[],
    totalCounts: { [key: string]: number },
    cdr: ChangeDetectorRef,
    workspaceRoleService?: WorkspaceRoleService,
    config: Partial<RegistrationsDataSourceCollectionConfig> = {},
    userContext?: { isAdmin: boolean; approvedBy: { name: string; uuid?: string; source: 'planning' } | null; displayName: string; email: string }
  ) {
    this._defaultConfig = { ...this._defaultConfig, ...config }
    this.registrationsDataSources = []
    let initializeFirstDataSource = true
    this._eventSlug = event.slug
    this._eventCheckinSpot = event.physical_type_id === 1 || event.physical_type_id === 3 ? 'global' : 'global_virtual'
    forms?.forEach((form) => {
      const parentForm = form.parent ? forms.find((f) => f.id === form.parent) : null
      if (parentForm) {
        parentForm['subForm'] = form
      }
      /**
       * To sort the parent form elements after parent id column
       */

      this.registrationsDataSources.push({
        registrationsDataSource: new RegistrationsDataSource(service, form, this._eventCheckinSpot, event, cdr, totalCounts[form.slug], workspaceRoleService, userContext),
        title: form?.name,
        weight: form?.weight?.toString() || '0',
        slug: form?.slug
      })
      this.get(form.slug)
        .registrationsDataSource.changed$.pipe(takeUntil(this._destroy$))
        .subscribe(() => this.changed$.next(null))
      this.get(form.slug).registrationsDataSource.initialize(
        {
          workspace: workspace,
          event: this._eventSlug,
          form: form.slug,
          ...this._defaultConfig
        },
        initializeFirstDataSource
      )
      if (initializeFirstDataSource) {
        initializeFirstDataSource = false
      }
    })
  }
}
